import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Row,
  Col,
  Avatar,
  Tag,
  Typography,
  Divider,
} from 'antd';
import {
  EditOutlined,
  UserOutlined,
  PlusOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  CreateCharacterStateRequest,
  KeyValuePair,
  CharacterStateWithRelationships,
} from '../api/models/Lifetree';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Method Selection Modal
interface MethodSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (method: 'manual' | 'ai') => void;
}

export const MethodSelectionModal: React.FC<MethodSelectionModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelectMethod 
}) => {
  return (
    <Modal
      title="Chọn phương thức tạo State"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Button
            type="primary"
            size="large"
            icon={<EditOutlined />}
            onClick={() => onSelectMethod('manual')}
            style={{ width: '100%', height: '60px', fontSize: '16px' }}
          >
            🖊️ Tạo thủ công
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Điền form thông tin chi tiết</div>
          </Button>
          
          <Button
            type="default"
            size="large"
            icon={<UserOutlined />}
            onClick={() => onSelectMethod('ai')}
            style={{ width: '100%', height: '60px', fontSize: '16px', borderColor: '#52c41a', color: '#52c41a' }}
          >
            🤖 Tạo bằng AI
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Mô tả và để AI tạo nguyên mẫu</div>
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

// AI Description Modal
interface AIDescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (description: string, previousStateId?: string) => Promise<void>;
  isLoading: boolean;
  characterName: string;
  availableStates: CharacterStateWithRelationships[];
}

export const AIDescriptionModal: React.FC<AIDescriptionModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading, 
  characterName, 
  availableStates 
}) => {
  const [form] = Form.useForm();
  const [description, setDescription] = useState('');
  const [previousStateId, setPreviousStateId] = useState<string | undefined>(undefined);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      await onSubmit(description, previousStateId);
      handleClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setDescription('');
    setPreviousStateId(undefined);
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '18px' }}>🤖</span>
          <span>Tạo State bằng AI cho: <strong>{characterName}</strong></span>
        </div>
      }
      open={isOpen}
      onCancel={handleClose}
      width={750}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isLoading} size="large">
          ✖️ Hủy bỏ
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={isLoading}
          icon={!isLoading ? <span style={{ marginRight: 4 }}>🚀</span> : undefined}
          size="large"
          style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            boxShadow: '0 4px 15px 0 rgba(102, 126, 234, 0.4)'
          }}
        >
          {isLoading ? 'AI đang tạo nguyên mẫu...' : 'Tạo nguyên mẫu bằng AI'}
        </Button>,
      ]}
      style={{ top: 20 }}
    >
      <Form form={form} layout="vertical">
        {availableStates.length > 0 && (
          <Form.Item 
            label={
              <span>
                <span style={{ marginRight: 8 }}>🔗 Phát triển từ state có sẵn</span>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                  (tùy chọn)
                </Text>
              </span>
            }
            help={
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 Chọn state trước đó để tạo state tiếp theo có tính liên tục, hoặc bỏ trống để tạo state hoàn toàn mới
              </Text>
            }
          >
            <Select
              placeholder="🎯 Chọn state làm cơ sở..."
              value={previousStateId}
              onChange={setPreviousStateId}
              allowClear
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {availableStates.map((state) => (
                <Option 
                  key={state.id} 
                  value={state.id}
                  label={`${state.name} (${state.age} tuổi)`}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 12,
                    padding: '4px 0'
                  }}>
                    {state.imageUrl ? (
                      <Avatar 
                        src={state.imageUrl} 
                        size={32} 
                        shape="square"
                        style={{ flexShrink: 0, borderRadius: 4 }}
                      />
                    ) : (
                      <Avatar 
                        icon={<UserOutlined />}
                        size={32} 
                        shape="square"
                        style={{ 
                          flexShrink: 0, 
                          borderRadius: 4,
                          backgroundColor: '#f0f0f0',
                          color: '#999'
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 500, 
                        color: '#262626',
                        marginBottom: 2
                      }}>
                        {state.name}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#8c8c8c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span>🎂 {state.age} tuổi</span>
                        <span>•</span>
                        <Tag                           
                          color={state.status === 'active' ? 'green' : 'orange'}
                          style={{ margin: 0, fontSize: '10px' }}
                        >
                          {state.status.toUpperCase()}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        
        <Divider style={{ margin: '16px 0' }} />
        
        <Form.Item 
          label={
            <span>
              <span style={{ marginRight: 8 }}>✍️ Mô tả chi tiết về state</span>
              <Text type="danger">*</Text>
            </span>
          }
          name="description"
          rules={[{ required: true, message: 'Vui lòng nhập mô tả chi tiết!' }]}
          help={
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {previousStateId 
                  ? "📈 Mô tả những thay đổi và phát triển từ state đã chọn: tuổi tác mới, thay đổi ngoại hình, tính cách, nghề nghiệp, phong cách giao tiếp..."
                  : "📝 Mô tả càng chi tiết càng tốt: tuổi tác, ngoại hình, tính cách, nghề nghiệp, phong cách giao tiếp, hoàn cảnh sống..."
                }
              </Text>
            </div>
          }
        >
          <TextArea 
            rows={previousStateId ? 6 : 8}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={previousStateId 
              ? `💡 Ví dụ: Sau 3 năm làm việc, ${characterName} giờ đã 28 tuổi và trở thành senior developer. Cậu tự tin hơn trong giao tiếp, dáng vẻ chững chạc hơn với kinh nghiệm làm việc. Tuy nhiên, áp lực công việc khiến cậu thường xuyên thức khuya và có thêm vài sợi tóc bạc. Phong cách ăn mặc cũng chuyên nghiệp hơn, thích đeo kính cận khi làm việc...`
              : `💡 Ví dụ: ${characterName} lúc này đã 25 tuổi, vừa tốt nghiệp thạc sĩ khoa học máy tính tại Stanford. Cậu cao khoảng 1m75, dáng người thon thả, da hơi ngăm do thường xuyên chạy bộ. Tóc đen cắt ngắn gọn gàng, đôi mắt nâu sáng thông minh. ${characterName} rất tự tin với khả năng lập trình của mình nhưng hơi ngại giao tiếp với người lạ, thích mặc áo hoodie và quần jeans...`
            }
            style={{ 
              fontSize: '14px',
              lineHeight: '1.6'
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Create State Modal
interface CreateStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCharacterStateRequest) => Promise<void>;
  isLoading: boolean;
  initialData?: Partial<CreateCharacterStateRequest>;
  characterName?: string;
}

export const CreateStateModal: React.FC<CreateStateModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading, 
  initialData, 
  characterName 
}) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<CreateCharacterStateRequest>({
    name: '',
    age: 0,
    alias: '',
    occupation: '',
    imageUrl: '',
    appearance: [{ key: '', value: '' }],
    innerSelf: [{ key: '', value: '' }],
    relationshipStyles: [{ key: '', value: '' }],
    description: '',
    displayedDescription: '',
  });
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (isOpen && initialData) {
      // Use AI data if available
      const newFormData = {
        name: initialData.name || '',
        age: initialData.age || 0,
        alias: initialData.alias || '',
        occupation: initialData.occupation || '',
        imageUrl: initialData.imageUrl || '',
        appearance: initialData.appearance && initialData.appearance.length > 0 ? initialData.appearance : [{ key: '', value: '' }],
        innerSelf: initialData.innerSelf && initialData.innerSelf.length > 0 ? initialData.innerSelf : [{ key: '', value: '' }],
        relationshipStyles: initialData.relationshipStyles && initialData.relationshipStyles.length > 0 ? initialData.relationshipStyles : [{ key: '', value: '' }],
        description: initialData.description || '',
        displayedDescription: initialData.displayedDescription || '',
      };
      setFormData(newFormData);
      
      form.setFieldsValue({
        name: newFormData.name,
        age: newFormData.age,
        alias: newFormData.alias,
        occupation: newFormData.occupation,
        description: newFormData.description,
        displayedDescription: newFormData.displayedDescription,
      });
    } else if (isOpen && !initialData) {
      // Fill default with character name if no initial data (manual creation)
      const defaultName = characterName || '';
      const emptyData = {
        name: defaultName,
        age: 0,
        alias: '',
        occupation: '',
        imageUrl: '',
        appearance: [{ key: '', value: '' }],
        innerSelf: [{ key: '', value: '' }],
        relationshipStyles: [{ key: '', value: '' }],
        description: '',
        displayedDescription: '',
      };
      setFormData(emptyData);
      form.setFieldsValue({
        name: defaultName,
        age: 0,
        alias: '',
        occupation: '',
        description: '',
        displayedDescription: '',
      });
    }
  }, [isOpen, initialData, characterName, form]);

  const handleSubmit = async () => {
    try {
      setErrorMessage('');
      await form.validateFields();
      
      const cleanData = {
        ...formData,
        appearance: formData.appearance.filter(item => item.key.trim() && item.value.trim()),
        innerSelf: formData.innerSelf.filter(item => item.key.trim() && item.value.trim()),
        relationshipStyles: formData.relationshipStyles.filter(item => item.key.trim() && item.value.trim()),
        description: formData.description?.trim() || undefined,
        displayedDescription: formData.displayedDescription?.trim() || undefined,
      };

      await onSubmit(cleanData);
      handleClose();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.messages?.[0] || error?.message || 'Có lỗi xảy ra khi tạo state';
      setErrorMessage(errorMsg);
      console.error('Form validation or API call failed:', error);
    }
  };

  const handleClose = () => {
    setErrorMessage('');
    form.resetFields();
    setFormData({
      name: '',
      age: 0,
      alias: '',
      occupation: '',
      imageUrl: '',
      appearance: [{ key: '', value: '' }],
      innerSelf: [{ key: '', value: '' }],
      relationshipStyles: [{ key: '', value: '' }],
      description: '',
      displayedDescription: '',
    });
    onClose();
  };

  const addKeyValuePair = (field: 'appearance' | 'innerSelf' | 'relationshipStyles') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], { key: '', value: '' }]
    }));
  };

  const updateKeyValuePair = (field: 'appearance' | 'innerSelf' | 'relationshipStyles', index: number, type: 'key' | 'value', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [type]: value } : item
      )
    }));
  };

  const removeKeyValuePair = (field: 'appearance' | 'innerSelf' | 'relationshipStyles', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <Modal
      title="Thêm State Mới"
      open={isOpen}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={isLoading}
        >
          Tạo State
        </Button>,
      ]}
    >
      {errorMessage && (
        <div style={{ 
          marginBottom: 16, 
          padding: '8px 12px', 
          backgroundColor: '#fff2f0', 
          border: '1px solid #ffccc7', 
          borderRadius: '6px',
          color: '#ff4d4f'
        }}>
          <Text type="danger">
            <strong>❌ Lỗi:</strong> {errorMessage}
          </Text>
        </div>
      )}
      
      <Form form={form} layout="vertical" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Form.Item 
          label="Tên" 
          name="name"
          rules={[{ required: true, message: 'Vui lòng nhập tên state!' }]}
        >
          <Input 
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Nhập tên state"
          />
        </Form.Item>

        <Form.Item 
          label="Tuổi" 
          name="age"
          rules={[{ required: true, message: 'Vui lòng nhập tuổi!' }]}
        >
          <InputNumber 
            min={0}
            max={120}
            value={formData.age}
            onChange={(value) => setFormData(prev => ({ ...prev, age: value || 0 }))}
            placeholder="Nhập tuổi"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label="Biệt danh" name="alias">
          <Input 
            value={formData.alias || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, alias: e.target.value }))}
            placeholder="Nhập biệt danh (không bắt buộc)"
          />
        </Form.Item>

        <Form.Item label="Nghề nghiệp" name="occupation">
          <Input 
            value={formData.occupation || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, occupation: e.target.value }))}
            placeholder="Nhập nghề nghiệp (không bắt buộc)"
          />
        </Form.Item>

        <Form.Item label="Ngoại hình" required>
          <Space direction="vertical" style={{ width: '100%' }}>
            {formData.appearance.map((item, index) => (
              <Row key={index} gutter={8} style={{ width: '100%' }}>
                <Col span={6}>
                  <Input
                    placeholder="Đặc điểm"
                    value={item.key}
                    onChange={(e) => updateKeyValuePair('appearance', index, 'key', e.target.value)}
                  />
                </Col>
                <Col span={16}>
                  <TextArea
                    placeholder="Mô tả"
                    value={item.value}
                    onChange={(e) => updateKeyValuePair('appearance', index, 'value', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                  />
                </Col>
                <Col span={2}>
                  <Button 
                    icon={<CloseOutlined />}
                    onClick={() => removeKeyValuePair('appearance', index)}
                    danger
                    size="small"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
            ))}
            <Button 
              type="dashed" 
              onClick={() => addKeyValuePair('appearance')}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Thêm đặc điểm ngoại hình
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Tâm lý bên trong" required>
          <Space direction="vertical" style={{ width: '100%' }}>
            {formData.innerSelf.map((item, index) => (
              <Row key={index} gutter={8} style={{ width: '100%' }}>
                <Col span={6}>
                  <Input
                    placeholder="Đặc điểm tâm lý"
                    value={item.key}
                    onChange={(e) => updateKeyValuePair('innerSelf', index, 'key', e.target.value)}
                  />
                </Col>
                <Col span={16}>
                  <TextArea
                    placeholder="Mô tả"
                    value={item.value}
                    onChange={(e) => updateKeyValuePair('innerSelf', index, 'value', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                  />
                </Col>
                <Col span={2}>
                  <Button 
                    icon={<CloseOutlined />}
                    onClick={() => removeKeyValuePair('innerSelf', index)}
                    danger
                    size="small"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
            ))}
            <Button 
              type="dashed" 
              onClick={() => addKeyValuePair('innerSelf')}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Thêm đặc điểm tâm lý
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Phong cách quan hệ" required>
          <Space direction="vertical" style={{ width: '100%' }}>
            {formData.relationshipStyles.map((item, index) => (
              <Row key={index} gutter={8} style={{ width: '100%' }}>
                <Col span={6}>
                  <Input
                    placeholder="Loại quan hệ"
                    value={item.key}
                    onChange={(e) => updateKeyValuePair('relationshipStyles', index, 'key', e.target.value)}
                  />
                </Col>
                <Col span={16}>
                  <TextArea
                    placeholder="Phong cách"
                    value={item.value}
                    onChange={(e) => updateKeyValuePair('relationshipStyles', index, 'value', e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                  />
                </Col>
                <Col span={2}>
                  <Button 
                    icon={<CloseOutlined />}
                    onClick={() => removeKeyValuePair('relationshipStyles', index)}
                    danger
                    size="small"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
            ))}
            <Button 
              type="dashed" 
              onClick={() => addKeyValuePair('relationshipStyles')}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Thêm phong cách quan hệ
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Mô tả chi tiết" name="description">
          <TextArea
            rows={3}
            value={formData.description || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Nhập mô tả chi tiết (không bắt buộc)"
          />
        </Form.Item>

        <Form.Item label="Mô tả hiển thị" name="displayedDescription">
          <TextArea
            rows={2}
            value={formData.displayedDescription || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, displayedDescription: e.target.value }))}
            placeholder="Nhập mô tả ngắn hiển thị (không bắt buộc)"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}; 